import { define, html, store } from "hybrids";

import ArtistVideo from "~/stores/ArtistVideo";
import Broadcast from "~/stores/Broadcast";
import Comment from "~/stores/Comment";
import Session from "~/stores/Session";
import {redirectToSignIn} from "~/utils/redirections";

export interface VBroadcastChatForm extends HTMLElement {
  comment: Comment;
  session: Session;
  context: Broadcast | ArtistVideo;
  contextType: "broadcast" | "video";
  quote?: Comment;
  input: HTMLElement;
  render: () => HTMLElement;
}

function clearQuote(host: VBroadcastChatForm) {
  host.quote = undefined;
}

function submit(host: VBroadcastChatForm, event: Event) {
  event.preventDefault();

  if (store.pending(host.comment) || !host.comment.content) return;

  store
    .submit(host.comment, {
      parent: host.quote,
      context: { identifier: host.context.id, type: host.contextType },
    })
    .then(() => {
      host.input.blur();

      clearQuote(host);
      // @ts-ignore
      host.comment = undefined;
    });
}

export default define<VBroadcastChatForm>({
  tag: "v-broadcast-chat-form",
  comment: store(Comment, { draft: true }),
  session: store(Session),
  context: undefined,
  contextType: "broadcast",
  quote: undefined,
  input: ({ render }) => render().querySelector("input") as HTMLInputElement,
  render: ({ comment, session, context, quote }) => html`
    <v-broadcast-container>
      <form onsubmit="${submit}" autocomplete="off">
        <v-ui-input>
          ${quote &&
          html` <div id="quote" slot="header">
            <v-broadcast-chat-comment
              comment="${quote}"
              context="${context}"
              quote
            ></v-broadcast-chat-comment>
            <v-ui-button
              icon="close"
              type="round"
              variant="outline"
              onclick="${clearQuote}"
            ></v-ui-button>
          </div>`}

          ${store.ready(session) &&
          session.user
          ? html`
            <input
              placeholder="Add a comment..."
              name="comment"
              value="${comment.content}"
              oninput="${html.set(comment, "content")}"
              readonly="${store.pending(comment)}"
            />

            <button
              type="submit"
              disabled="${store.pending(comment)}"
              slot="footer"
            >
              <v-ui-button
                icon="send"
                type="round"
                pending="${store.pending(comment)}"
              ></v-ui-button>
            </button>`
          : html`
            <input
              placeholder="Add a comment..."
              name="comment"
              onfocus="${redirectToSignIn}"
              readonly="true"
            />
          `}  
        </v-ui-input>
        ${store.error(comment) &&
        comment.content &&
        html`
          <v-ui-text color="text-error" ygutter="1">
            We couldn't deliver your message, try again...
          </v-ui-text>
        `}
      </form>
    </v-broadcast-container>
  `.css`
    :host {
      position: relative;
      z-index: var(--v-ui-z-index-fixed);
      height: 83px;
      display: grid;
      grid: 1fr / auto;
    }

    v-broadcast-container {
      position: fixed;
      bottom: calc(73px + var(--v-ui-safe-area-inset-bottom));
      left: 0;
      right: 0;
      border-top: 1px solid rgba(128, 129, 145, 0.1);
      background: rgba(11, 13, 19, 0.9);
      -webkit-backdrop-filter: blur(10px) saturate(100%);
      backdrop-filter: blur(10px) saturate(100%);
      transition: bottom 0.2s ease-out;
    }

    v-ui-input {
      display: grid;
      grid: auto / 1fr auto;
      grid-gap: 10px 8px;
      padding: 12px 14px;
      border: 1px solid rgba(255, 255, 255, 0.1);
      background: rgba(0, 0, 0, 0.3);
      border-radius: 8px;
    }

    form:focus-within {
      border-color: rgba(255, 255, 255, 0.3);
    }

    #quote {
      grid-area: 1 / 1 / 1 / 3;
      display: grid;
      grid: auto / 1fr auto;
      align-items: flex-start;
    }

    button, v-ui-button {
      background: none;
      border: none;
      padding: 0;
      margin: 0;
      width: 24px;
      height: 24px;
    }

    @media (min-width: 931px) {
      :host {
        height: auto;
      }
    
      v-broadcast-container {
        position: static;
        bottom: auto;
        background: none;
        -webkit-backdrop-filter: none;
        backdrop-filter: none;
      }
    }
  `,
});
